import React, { useState, useRef, useMemo, useCallback } from "react";
// Asset imports
import { JupyterNotebookViewer } from "react-jupyter-notebook-viewer";
import BitBotNotebook from "../../Assets/NoteBooks/BitBot.ipynb";
import DoctorRosterNotebook from "../../Assets/NoteBooks/DoctorRoster.ipynb";
import CovidPredictorNotebook from "../../Assets/NoteBooks/CovidPredictor.ipynb";

// A simple generic icon for notebooks, memoized for performance.
// React.memo prevents re-render if props (className, color) haven't changed.
const NotebookIcon = React.memo(({ className, color = "currentColor" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true" // Decorative icon
  >
    <path d="M2 6s.5-1 2-1h16c1.5 0 2 1 2 1v14s-.5 1-2 1H4c-1.5 0-2-1-2-1Z"></path>
    <path d="M4 6V4c0-1 .9-2 2-2h10c.6 0 1 .4 1 1"></path>
  </svg>
));
NotebookIcon.displayName = "NotebookIcon"; // Enhances debuggability

// Static data defined outside the component to prevent re-creation on renders.
const notebooksData = [
  {
    value: "BitBot",
    label: "BitBot: Crypto Analysis",
    notebook: BitBotNotebook,
    description: "A Jupyter notebook detailing the BitBot crypto analysis project.",
  },
  {
    value: "RoctorDostor", // Note: Original value. If a typo, "DoctorRoster" might be intended.
    label: "Doctor Roster Management",
    notebook: DoctorRosterNotebook,
    description: "Notebook for the Doctor Roster management system.",
  },
  {
    value: "CovidPredictor",
    label: "COVID-19 Predictor",
    notebook: CovidPredictorNotebook,
    description: "Analysis and prediction models related to COVID-19.",
  },
];

// Constants for icon colors for clarity and maintainability
const ACTIVE_ICON_COLOR = "gray"; // CSS keyword 'gray'
const INACTIVE_ICON_COLOR = "rgb(107, 114, 128)"; // Tailwind's gray-500

function DS() {
  const [activeNotebookValue, setActiveNotebookValue] = useState(notebooksData[0].value);
  const mainContentRef = useRef(null);

  // Memoize activeNotebook to avoid re-calculating on every render
  // unless activeNotebookValue (its dependency) changes.
  const activeNotebook = useMemo(() =>
    notebooksData.find(nb => nb.value === activeNotebookValue),
    [activeNotebookValue]
  );

  // Memoize handleNotebookChange to stabilize its reference.
  // Useful if passed to memoized children, and good practice.
  // Empty dependency array is correct: setActiveNotebookValue is stable,
  // and mainContentRef (the ref object itself) is stable.
  const handleNotebookChange = useCallback((newValue) => {
    setActiveNotebookValue(newValue);
    if (mainContentRef.current) {
      // Reset scroll position of the main content area
      mainContentRef.current.scrollTop = 0;
    }
  }, []); // No dependencies needed as setters from useState are stable and ref objects are stable

  // Styling functions:
  // These are re-created on each render, which is acceptable for simple functions like these.
  // Their logic depends on activeNotebookValue, ensuring styles update correctly.
  // Memoizing them with useCallback is possible but likely unnecessary overhead here
  // unless they become very complex or are passed to deeply nested React.memo components.
  const getButtonStyles = (buttonValue) => {
    const isActive = activeNotebookValue === buttonValue;
    // Base classes applied to all buttons
    let baseClasses =
      "flex items-center p-3 rounded-lg transition-colors duration-150 ease-in-out cursor-pointer w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-800";
    
    if (isActive) {
      // Active button specific styles
      return `${baseClasses} bg-gray-100 dark:bg-gray-700 border-2 border-gray-500 dark:border-gray-400 focus:ring-gray-500`;
    }
    // Inactive button specific styles
    return `${baseClasses} bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500 focus:ring-indigo-500`;
  };

  const getTextColorAndWeightClass = (buttonValue) => {
    const isActive = activeNotebookValue === buttonValue;
    // Text color and font weight classes based on active state
    const textColorClass = isActive ? "text-gray-700 dark:text-gray-200" : "text-gray-700 dark:text-gray-300";
    const fontWeightClass = isActive ? "font-semibold" : "font-medium";
    return `${textColorClass} ${fontWeightClass}`;
  };

  // Configuration for sticky sidebar positioning and max height
  // These are constant strings, so defining them here is fine.
  const fixedSidebarTopOffset = "md:top-16"; // Corresponds to 4rem
  const sidebarMaxHeight = "md:max-h-[calc(100vh-4rem-2rem)]"; // Viewport height - top offset - bottom spacing

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex flex-col">
      <header className="py-6 md:py-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-16">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white project-heading">
            Data Science Notebooks
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300 text-base md:text-lg">
            Explore various data science projects presented as interactive Jupyter Notebooks.
          </p>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 md:py-8 gap-8 md:gap-10">
        <nav
          // Removed self-start to allow nav to stretch on mobile
          // Retain desktop widths, sticky behavior, and shrink-0
          className={`md:w-72 lg:w-1/4 shrink-0 md:sticky ${fixedSidebarTopOffset} ${sidebarMaxHeight} md:overflow-y-auto`}
          aria-labelledby="notebook-navigation-title"
        >
          <div className="space-y-3">
            <h2 id="notebook-navigation-title" className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3 hidden md:block">
              Select Notebook
            </h2>
            {notebooksData.map((notebook) => {
              const isActive = activeNotebookValue === notebook.value;
              return (
                <button
                  key={notebook.value} // Essential for React's list reconciliation
                  type="button"
                  className={getButtonStyles(notebook.value)} // This already includes w-full
                  onClick={() => handleNotebookChange(notebook.value)}
                  aria-pressed={isActive} // Indicates toggle state for accessibility
                >
                  <NotebookIcon
                    className="mr-2.5 flex-shrink-0 w-5 h-5"
                    color={isActive ? ACTIVE_ICON_COLOR : INACTIVE_ICON_COLOR}
                  />
                  <span className={`text-sm ${getTextColorAndWeightClass(notebook.value)}`}>
                    {notebook.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <main
          ref={mainContentRef}
          className="flex-1 min-w-0 md:overflow-y-auto" // Allow main content to scroll independently on medium+ screens
        >
          {activeNotebook && ( // Conditional rendering: only show if a notebook is selected
            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{activeNotebook.label}</h3>
                  {activeNotebook.description && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{activeNotebook.description}</p>
                  )}
              </div>
              <div className="p-1 md:p-2 lg:p-4 text-left min-h-[400px]"> {/* Padding around viewer, min-height for initial layout */}
                <JupyterNotebookViewer
                  filePath={activeNotebook.notebook}
                  // Key is crucial: ensures the component re-mounts when the notebook changes,
                  // which is often necessary for complex viewers to reset internal state.
                  key={activeNotebook.value} 
                  notebookInputLanguage="python"
                  // Consider making theme props dynamic based on a site-wide dark mode setting if available.
                  inputCodeDarkTheme={true}
                  outputDarkTheme={true}
                  inputMarkdownDarkTheme={true}
                  showInputLineNumbers={true}
                  showOutputLineNumbers={false}
                  withOnClick={true} // Enables cell interactions
                  // className="w-full" // Usually not needed as viewer should adapt to container width.
                                    // Custom styling might be applied here if needed.
                />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default DS;